/**
 * @file User interface components for GDPR-compliant consent management.
 * @summary Provides consent banner, settings modal, and blocked content placeholders
 * with full accessibility support and responsive design.
 *
 * Depends on the blocker script for consent state management and event coordination.
 * Uses event-driven architecture to maintain loose coupling between UI and core logic.
 * Implements WCAG guidelines for keyboard navigation and screen reader compatibility.
 *
 * @version 3.3.9
 * @author University College London
 */

/*
 * Import platform mapping data for user-friendly service name display.
 * Externalised to JSON file for easier maintenance and brand name updates.
 * Extended with user configuration to support custom service name mappings.
 */
import platformMapping from './domains/platform-mapping.json';

// Extended platform mapping combines built-in and user-defined service names.
const extendedPlatformMapping = { ...platformMapping };

(function() {
    'use strict';

    /**
     * Ensures proper initialisation order with the blocking engine.
     * UI components require the blocker's API and configuration to function correctly.
     * Event-driven approach prevents race conditions during script loading sequences
     * and enables flexible deployment strategies such as deferred UI loading.
     *
     * Handles both synchronous (blocker loaded first) and asynchronous (event-driven)
     * initialisation patterns to support different CDN deployment configurations.
     *
     * @returns {void} Initialises UI when blocker is ready.
     */
    function initializeWhenReady() {
        if (window.UCLPrivacy) {
            /*
             * Blocker already initialised — direct configuration access.
             * Synchronous platform mapping integration for immediate UI setup
             * when blocker script loads before UI script in deployment scenarios.
             */
            if (window.privacyConfig && window.privacyConfig.platformMapping) {
                Object.assign(extendedPlatformMapping, window.privacyConfig.platformMapping);
            }
            initializeUI(window.UCLPrivacy);
        } else {
            /*
             * Event-driven platform mapping integration for asynchronous loading.
             * Receives validated configuration data from blocker script via custom
             * event to maintain security boundaries and prevent race conditions.
             * Essential for deferred UI loading scenarios and CDN deployments.
             */
            window.addEventListener('ucl-privacy-blocker-ready', (event) => {
                if (event.detail && event.detail.platformMapping) {
                    Object.assign(extendedPlatformMapping, event.detail.platformMapping);
                }
                initializeUI(window.UCLPrivacy);
            });
        }
    }

    /**
     * Primary UI initialisation orchestrator for consent interface components.
     * Establishes complete user interface including banner, modal, settings controls,
     * and accessibility features. Coordinates with stored consent state to determine
     * appropriate initial UI visibility and behaviour patterns.
     *
     * Sets up event handlers, placeholder generation, content restoration, and
     * cross-component communication for a fully functional consent management interface.
     *
     * @param {object} UCLPrivacy - Blocker API providing consent management and configuration.
     * @param {function} UCLPrivacy.getConsent - Returns current consent state.
     * @param {function} UCLPrivacy.hasUserConsented - Checks if user has made consent decisions.
     * @param {function} UCLPrivacy.updateConsent - Updates single consent category.
     * @param {function} UCLPrivacy.updateAllConsent - Batch updates all consent categories.
     * @param {object} UCLPrivacy.config - Complete configuration object with options and patterns.
     * @returns {void} Initialises complete UI system.
     */
    function initializeUI(UCLPrivacy) {
        const { config, getConsent, hasUserConsented, updateConsent, updateAllConsent } = UCLPrivacy;

        // UI state management prevents animation conflicts and race conditions.
        let bannerVisible = false;
        let bannerElement = null;
        let settingsBtnElement = null;
        let modalElement = null;

        let bannerTransitioning = false;
        let modalTransitioning = false;

        // Cache prevents duplicate placeholders and reduces DOM queries.
        const placeholderCache = new Set();

        // Cache compiled URL patterns for performance - prevents repeated regex compilation.
        // Defined at function scope to persist across evaluateButtonConfig() calls.
        const PATTERN_CACHE_MAX_SIZE = 25;
        const patternCache = new Map();

        /*
         * Track restoration resources for proper cleanup.
         * Maintains references to timeouts, event listeners, and blob URLs
         * to ensure they are properly cleaned up and prevent memory leaks.
         */
        const restorationTracker = {
            activeTimeouts: new Map(),      // Track active timeout IDs for cleanup.
            blobUrls: new Set(),            // Track created blob URLs for revocation.
            eventListeners: new WeakMap(),  // Track event listeners for removal.

            addTimeout: function(key, timeoutId) {
                this.activeTimeouts.set(key, timeoutId);
            },

            clearTimeout: function(key) {
                const timeoutId = this.activeTimeouts.get(key);
                if (timeoutId) {
                    clearTimeout(timeoutId);
                    this.activeTimeouts.delete(key);
                }
            },

            addBlobUrl: function(url) {
                this.blobUrls.add(url);
            },

            revokeBlobUrl: function(url) {
                if (this.blobUrls.has(url)) {
                    try {
                        URL.revokeObjectURL(url);
                        this.blobUrls.delete(url);
                    } catch (e) {
                        debug('Error revoking blob URL:', url, e);
                    }
                }
            },

            trackEventListener: function(element, listener) {
                if (!this.eventListeners.has(element)) {
                    this.eventListeners.set(element, new Set());
                }
                this.eventListeners.get(element).add(listener);
            },

            removeEventListeners: function(element) {
                const listeners = this.eventListeners.get(element);
                if (listeners) {
                    listeners.forEach(listener => {
                        try {
                            element.removeEventListener('load', listener);
                            element.removeEventListener('error', listener);
                        } catch (e) {
                            debug('Error removing event listener:', e);
                        }
                    });
                    this.eventListeners.delete(element);
                }
            },

            cleanup: function() {
                // Clear timeouts.
                this.activeTimeouts.forEach((timeoutId) => {
                    clearTimeout(timeoutId);
                });
                this.activeTimeouts.clear();

                // Revoke blob URLs.
                this.blobUrls.forEach(url => {
                    try {
                        URL.revokeObjectURL(url);
                    } catch (e) {
                        debug('Error revoking blob URL during cleanup:', url, e);
                    }
                });
                this.blobUrls.clear();

                // WeakMap listeners auto-collected on element removal.
                debug('Restoration tracker cleanup completed');
            }
        };

        /**
         * UI-specific logging for interface debugging and troubleshooting.
         * Maintains separation from core blocker logging to enable focused
         * interface debugging without noise from consent logic operations.
         *
         * Only logs when logging option is enabled in configuration to prevent
         * performance impact and console noise in production environments.
         *
         * @param {...*} args - Log messages and contextual debugging data.
         * @returns {void} Logs messages to console when enabled.
         */
        function log(...args) {
            if (config.options.logging) {
                console.log('[UCL Privacy UI]', ...args);
            }
        }

        /**
         * Debug logging for detailed troubleshooting information.
         * Used for development, script restoration debugging, and performance analysis.
         * More verbose than standard logging and includes timing information.
         *
         * Only active when debug option is enabled to avoid production overhead
         * and potential security information leakage in browser developer tools.
         *
         * @param {...*} args - Debug messages and contextual data.
         * @returns {void} Logs debug messages to console when debug mode enabled.
         */
        function debug(...args) {
            if (config.options.debug) {
                console.log('[UCL Privacy UI Debug]', ...args);
            }
        }

        /**
         * Converts glob pattern with wildcards to regex and tests against pathname.
         * Supports * (any characters) wildcards for simple pattern matching.
         *
         * Pattern compilation is cached in 'patternCache'.
         * First match compiles and caches; subsequent matches use cached regex.
         * Cache uses FIFO eviction when full (PATTERN_CACHE_MAX_SIZE = 25).
         *
         * @param {string} pattern - Glob pattern with * wildcards (e.g., '/admin/*').
         * @param {string} pathname - URL pathname to test (e.g., '/admin/users').
         * @returns {boolean} True if pathname matches pattern; false on invalid input or regex error.
         */
        function matchesPattern(pattern, pathname) {
            if (!pattern || !pathname) return false;

            try {
                let regex = patternCache.get(pattern);
                if (!regex) {
                    // Escape regex special chars except *
                    const escapedPattern = pattern
                        .replace(/[.+?^${}()|[\]\\]/g, '\\$&')  // Escape special regex chars.
                        .replace(/\*/g, '.*');                    // Convert * to .*

                    regex = new RegExp(`^${escapedPattern}$`);

                    // Bounded cache: evict oldest entry if at capacity (FIFO eviction).
                    // Prevents unbounded memory growth on long-running pages or SPAs.
                    if (patternCache.size >= PATTERN_CACHE_MAX_SIZE) {
                        const oldestKey = patternCache.keys().next().value;
                        patternCache.delete(oldestKey);
                        debug(`Pattern cache evicted oldest entry: "${oldestKey}"`);
                    }
                    patternCache.set(pattern, regex);
                }

                const matches = regex.test(pathname);

                debug(`Pattern match: "${pattern}" vs "${pathname}" = ${matches}`);

                return matches;
            } catch (e) {
                console.warn('[UCL Privacy UI] Invalid pattern:', pattern, e);
                return false;
            }
        }

        /**
         * Evaluates urlConditions and returns effective button configuration.
         * Last matching rule wins. Missing properties inherit from defaults via cascade:
         * System defaults → User defaults → Matched rule.
         *
         * @param {object} btnConfig - Settings button configuration from validated config.
         * @param {string} pathname - Current URL pathname (e.g., '/admin/users').
         * @returns {{hidden: boolean, position: {side: string, bottom: string, horizontal: string, zIndex: number}}}
         *          Effective configuration with visibility and fully-resolved position.
         */
        function evaluateButtonConfig(btnConfig, pathname) {
            try {
                const systemDefaults = {
                    side: 'right',
                    bottom: '20px',
                    horizontal: '20px',
                    zIndex: 999997
                };

                // User defaults (level 2 of cascade).
                const userDefaults = { ...systemDefaults, ...(btnConfig.position || {}) };

                // Start with user defaults.
                const effectiveConfig = {
                    hidden: btnConfig.visibility?.hidden || false,
                    position: userDefaults
                };

                // If globally hidden, return immediately.
                if (effectiveConfig.hidden) {
                    return effectiveConfig;
                }

                // Process urlConditions (last match wins).
                if (Array.isArray(btnConfig.urlConditions)) {
                    let lastMatch = null;

                    for (const condition of btnConfig.urlConditions) {
                        if (matchesPattern(condition.pattern, pathname)) {
                            lastMatch = condition;
                            debug(`URL condition matched: ${condition.pattern}`);
                        }
                    }

                    // Apply last matching rule (level 3 of cascade).
                    if (lastMatch) {
                        // Merge position properties (inheritance).
                        if (lastMatch.position) {
                            effectiveConfig.position = {
                                ...effectiveConfig.position,
                                ...lastMatch.position
                            };
                        }

                        // Apply per-rule visibility.
                        if (lastMatch.visibility && lastMatch.visibility.hidden !== undefined) {
                            effectiveConfig.hidden = lastMatch.visibility.hidden;
                        }
                    }
                }

                return effectiveConfig;
            } catch (error) {
                console.error('[UCL Privacy UI] Error evaluating button config:', error);
                // Return system defaults on error.
                return {
                    hidden: false,
                    position: {
                        side: 'right',
                        bottom: '20px',
                        horizontal: '20px',
                        zIndex: 999997
                    }
                };
            }
        }

        /*
         * Platform name mapping for enhanced user experience and trust.
         * Imported from external JSON file for easier maintenance and brand updates.
         * Converts technical hostnames to recognisable service names in placeholder
         * messages and consent interfaces, significantly improving user comprehension.
         */

        /**
         * Creates readable platform names from unknown hostnames with security validation.
         * Implements comprehensive sanitisation to prevent XSS attacks whilst generating
         * user-friendly display names. Handles edge cases including malformed hostnames,
         * injection attempts, and unusual domain structures with consistent fallback behaviour.
         *
         * @param {string} hostname - Raw hostname requiring sanitisation and formatting.
         * @returns {string} Security-validated, capitalised platform name safe for display.
         */
        function sanitizePlatformName(hostname) {
            if (!hostname || typeof hostname !== 'string') return 'Unknown';
            const cleanHostname = hostname.toLowerCase().replace(/^www\./, '').replace(/[^a-zA-Z0-9.-]/g, '').slice(0, 50);  // Remove potentially dangerous characters and enforce length limits.
            const domain = cleanHostname.split('.')[0];
            return domain && domain.length > 0 ? domain.charAt(0).toUpperCase() + domain.slice(1) : 'Unknown';
        }

        /**
         * Resolves platform names with comprehensive fallback strategy and error handling.
         * Prioritises known service mappings for accuracy and user recognition, then
         * applies security-conscious sanitisation for unknown services. Handles malformed
         * URLs gracefully to prevent UI breakage from invalid or malicious embedded content.
         *
         * Platform mapping priority:
         * 1. Extended mapping (user configuration + built-in mappings)
         * 2. Subdomain matching for service variants and CDNs
         * 3. Security-validated hostname sanitisation for unknowns
         *
         * @param {string} url - Embedded content URL requiring platform identification.
         * @returns {string} Human-readable, security-validated platform name for display.
         */
        function getPlatformName(url) {
            try {
                const hostname = new URL(url).hostname.toLowerCase();

                // Priority 1: Exact hostname match in extended mapping.
                if (extendedPlatformMapping[hostname]) {
                    return extendedPlatformMapping[hostname];
                }

                /*
                 * Priority 2: Subdomain matching for service variants and CDNs.
                 * Handles cases like 'player.vimeo.com' matching 'vimeo.com' mapping
                 * or 'www.youtube.com' matching 'youtube.com' configuration.
                 */
                for (const [domain, name] of Object.entries(extendedPlatformMapping)) {
                    if (hostname.includes(domain)) {
                        return name;
                    }
                }

                // Priority 3: Security-validated sanitisation for unknown services.
                return sanitizePlatformName(hostname);
            } catch (e) {
                /*
                 * Malformed URL fallback prevents UI breakage from invalid embedded
                 * content or malicious injection attempts through URL parameters.
                 */
                return 'Unknown Platform';
            }
        }

        /**
         * Generates secure privacy policy section for GDPR transparency compliance.
         * Uses secure DOM manipulation instead of innerHTML to prevent XSS vulnerabilities
         * whilst maintaining full accessibility standards. Creates semantically structured
         * inline content with proper ARIA labelling and comprehensive keyboard navigation support.
         *
         * Essential for GDPR Article 13 transparency obligations regarding data processing
         * purposes and user rights. Handles both banner and modal contexts with context-appropriate
         * styling classes and responsive design considerations.
         *
         * @param {string} context - UI context identifier: 'banner' or 'modal' for styling.
         * @returns {DocumentFragment|null} Secure privacy policy DOM fragment or null if disabled.
         */
        function createPrivacyPolicySection(context) {
            try {
                /*
                 * Input validation prevents function misuse and ensures proper styling application.
                 * Context parameter determines appropriate CSS class selection and layout structure
                 * for optimal presentation in different UI contexts.
                 */
                if (!context || typeof context !== 'string') {
                    console.warn('[UCL Privacy UI] createPrivacyPolicySection: Invalid context provided');
                    return null;
                }

                /*
                 * Configuration validation ensures privacy policy section only appears
                 * when properly configured with valid URLs. Prevents broken links,
                 * incomplete information, or security vulnerabilities from malformed configuration.
                 */
                const privacyPolicy = config?.privacyPolicySection;
                if (!privacyPolicy || !privacyPolicy.enabled || !privacyPolicy.url) {
                    return null;
                }

                /*
                 * Document fragment approach optimises DOM performance by enabling
                 * complex structure building in memory before single insertion operation.
                 * Reduces layout thrashing and improves rendering performance.
                 */
                const fragment = document.createDocumentFragment();

                /*
                 * BEM methodology ensures consistent styling and maintainable CSS architecture
                 * across different UI contexts. ARIA attributes provide screen reader users
                 * with comprehensive semantic information for accessible navigation.
                 */
                const container = document.createElement('div');
                const baseClass = context === 'banner' ? 'ucl-privacy-banner' : 'ucl-privacy-modal';
                container.className = `${baseClass}__policy-section`;
                container.setAttribute('role', 'complementary');
                container.setAttribute('aria-label', 'Privacy policy information');

                // Create inner wrapper div for modal context to maintain consistent BEM structure.
                let contentWrapper = container;
                if (context === 'modal') {
                    const innerWrapper = document.createElement('div');
                    innerWrapper.className = `${baseClass}__policy-wrapper`;
                    container.appendChild(innerWrapper);
                    contentWrapper = innerWrapper;
                }

                // Create inline content wrapper for horizontal layout presentation.
                const inlineWrapper = document.createElement('p');
                inlineWrapper.className = `${baseClass}__policy-inline`;

                // Create inline title element if configured in privacy policy settings.
                if (privacyPolicy.title) {
                    const title = document.createElement('span');
                    title.className = `${baseClass}__policy-title`;
                    title.textContent = privacyPolicy.title;
                    inlineWrapper.appendChild(title);

                    // Add typographic colon separator for title-description relationship.
                    const colonSeparator = document.createElement('span');
                    colonSeparator.className = `${baseClass}__policy-separator`;
                    colonSeparator.textContent = ': ';
                    inlineWrapper.appendChild(colonSeparator);
                }

                // Create inline description element if configured in privacy policy settings.
                if (privacyPolicy.description) {
                    const description = document.createElement('span');
                    description.className = `${baseClass}__policy-description`;
                    description.textContent = privacyPolicy.description;
                    inlineWrapper.appendChild(description);

                    // Add space separator between description and link elements.
                    const dashSeparator = document.createElement('span');
                    dashSeparator.className = `${baseClass}__policy-separator`;
                    dashSeparator.textContent = ' ';
                    inlineWrapper.appendChild(dashSeparator);
                }

                // Create accessible privacy policy link with security attributes.
                const link = document.createElement('a');
                link.className = `${baseClass}__policy-link`;
                link.href = privacyPolicy.url;
                link.textContent = privacyPolicy.linkText || 'Read our privacy policy';
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                link.setAttribute('aria-label', `Open privacy policy in new tab: ${privacyPolicy.linkText || 'privacy policy'}`);

                // Ensure explicit keyboard focusability for accessibility compliance.
                link.tabIndex = 0;

                /*
                 * Enhanced keyboard accessibility for privacy policy links.
                 * Implements standard keyboard interaction patterns with both Enter and Space
                 * key activation. Uses requestAnimationFrame for proper focus management
                 * to ensure focus persistence after potential DOM updates.
                 */
                link.addEventListener('keydown', (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        link.click();

                        /*
                         * Asynchronous focus management prevents focus loss during DOM updates.
                         * Defensive existence check ensures element remains in DOM before
                         * attempting focus restoration for robust interaction handling.
                         */
                        requestAnimationFrame(() => {
                            if (link && document.contains(link)) {
                                link.focus();
                            }
                        });
                    }
                });

                inlineWrapper.appendChild(link);
                contentWrapper.appendChild(inlineWrapper);
                fragment.appendChild(container);

                return fragment;

            } catch (error) {
                console.warn('[UCL Privacy UI] createPrivacyPolicySection: Error creating privacy policy section:', error.message);
                return null;
            }
        }

        /**
         * Hides original lazy-load content to prevent visual confusion with consent gates.
         * Searches for common oEmbed lazy-load wrapper classes and preview elements,
         * then hides them to ensure users see only the consent gate interface.
         * Essential for preventing dual UI states that could confuse user consent decisions.
         *
         * @param {HTMLIFrameElement} iframe - The blocked iframe requiring content hiding.
         */
        function hideOriginalContent(iframe) {
            // Look for common oEmbed wrapper patterns
            const wrapper = iframe.closest('.oembed-lazyload, .embed-container, .media-embed, .lazy-embed');

            if (wrapper) {
                // Hide preview content whilst preserving iframe and consent gate visibility.
                const previewElements = wrapper.querySelectorAll(
                    '.oembed-lazyload__button, .oembed-lazyload__picture, .embed-preview, .lazy-preview, a[class*="oembed"], img[class*="oembed"]'
                );
                previewElements.forEach(el => {
                    if (el !== iframe && !el.classList.contains('ucl-privacy-placeholder')) {
                        el.style.display = 'none';
                    }
                });
            }
        }

        /**
         * Creates informative placeholders for blocked embedded content with deduplication.
         * Transforms blank blocked iframes into user-friendly consent gates featuring
         * clear explanations, direct access links, and immediate consent action buttons.
         * Significantly improves user experience by explaining blocking rationale and
         * providing immediate resolution pathways whilst preventing duplicate placeholders.
         *
         * Implements comprehensive deduplication logic to handle dynamic content loading
         * and prevent multiple placeholders for the same blocked resource.
         *
         * @param {HTMLIFrameElement} iframe - Blocked iframe requiring placeholder interface.
         * @returns {HTMLElement} Accessible placeholder with consent restoration capability.
         */
        function createIframePlaceholder(iframe) {
            if (!config.options.enablePlaceholders) {
                return document.createComment('Placeholder disabled');
            }
            const src = iframe.getAttribute('data-blocked-src');
            /*
             * data-blocked-actual-url contains the real third-party URL when proxy detection
             * identifies a first-party domain serving external content (e.g., /oembed/proxy?url=youtube.com).
             * Set by blocker when blockFirstPartyProxies is enabled and a proxy pattern is detected.
             * Format: Full URL string of the actual third-party destination (e.g., 'https://youtube.com/watch?v=...')
             * Used to show users the true platform being blocked rather than the confusing proxy URL.
             */
            const actualUrl = iframe.getAttribute('data-blocked-actual-url');
            const purpose = iframe.getAttribute('data-blocked-purpose');
            const purposeType = purpose || 'embeds';

            // Prioritise actual destination URL for platform detection, fallback to proxy URL.
            const platformDetectionUrl = actualUrl || src;
            const platformName = getPlatformName(platformDetectionUrl);

            // Display actual destination URL to users when available, fallback to proxy URL.
            const displayUrl = actualUrl || src;

            const container = document.createElement('div');
            container.className = 'ucl-privacy-placeholder ucl-privacy-wrapper ucl-consent-gate';
            container.setAttribute('data-placeholder-for', src);
            container.setAttribute('role', 'region');
            container.setAttribute('aria-label', `Blocked ${platformName} content`);

            const title = document.createElement('h3');
            title.className = 'ucl-consent-gate__title';
            /*
             * Inline SVG prevents additional HTTP requests and ensures consistent rendering
             * across all network conditions and CDN configurations. Critical for maintaining
             * placeholder reliability and visual consistency regardless of external dependencies.
             */
            const iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            iconSvg.setAttribute('fill', 'none');
            iconSvg.setAttribute('viewBox', '0 0 24 24');
            iconSvg.setAttribute('stroke-width', '1.5');
            iconSvg.setAttribute('stroke', 'currentColor');
            iconSvg.style.cssText = 'width: 20px; height: 20px; display: inline-block; vertical-align: middle; margin-right: 8px;';
            const iconPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            iconPath.setAttribute('stroke-linecap', 'round');
            iconPath.setAttribute('stroke-linejoin', 'round');
            iconPath.setAttribute('d', 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z');
            iconSvg.appendChild(iconPath);
            title.appendChild(iconSvg);
            title.appendChild(document.createTextNode('Third-party hosted content'));

            const description = document.createElement('p');
            description.className = 'ucl-consent-gate__description';
            const platformElement = document.createElement('strong');
            platformElement.textContent = platformName;
            description.append('This content is hosted on ', platformElement, '. Third-party embedded content may include its own cookies for analytics, marketing, and functionality. Please accept embedded content cookies to view this content or access it directly.');  // Clear explanation of data processing implications for GDPR compliance.

            const directLink = document.createElement('a');
            directLink.className = 'ucl-consent-gate__link';
            directLink.href = displayUrl;
            directLink.target = '_blank';
            directLink.rel = 'noopener noreferrer';
            directLink.textContent = displayUrl;
            directLink.setAttribute('aria-label', `Open ${platformName} content in new tab`);

            const button = document.createElement('button');
            button.className = 'ucl-consent-gate__button ucl-primary-button';
            button.textContent = 'Accept embedded content cookies';
            button.type = 'button';
            button.setAttribute('aria-label', `Accept embedded content cookies to load ${platformName} content`);
            /*
             * Direct consent update triggers both content restoration and UI synchronisation.
             * Provides immediate user feedback and resolves the blocking situation without
             * requiring navigation to settings modal for streamlined user experience.
             */
            button.addEventListener('click', async (e) => {
                // Handle async consent updates.
                const clickedButton = e.target;
                const originalText = clickedButton.textContent;

                try {
                    clickedButton.disabled = true;
                    clickedButton.textContent = 'Enabling...';

                    const success = await updateConsent(purposeType, true);

                    if (!success) {
                        // Show error in placeholder.
                        clickedButton.textContent = 'Error - Try Again';
                        setTimeout(() => {
                            clickedButton.textContent = originalText;
                            clickedButton.disabled = false;
                        }, 3000);

                        console.error(`Failed to enable ${purposeType} consent from placeholder`);
                    }
                    // Success handled by consent update event.
                } catch (error) {
                    console.error('Critical error enabling consent from placeholder:', error);
                    clickedButton.textContent = 'Error - Try Again';
                    setTimeout(() => {
                        clickedButton.textContent = originalText;
                        clickedButton.disabled = false;
                    }, 3000);
                }
            });

            container.append(title, description, directLink, button);

            // Hide original content to prevent confusion.
            hideOriginalContent(iframe);

            // Cache this placeholder to avoid duplicate DOM queries.
            placeholderCache.add(src);

            return container;
        }

        /**
         * Constructs complete consent UI with comprehensive accessibility and security considerations.
         * Builds banner, modal, settings controls, and persistent access button with proper ARIA
         * attributes, keyboard navigation support, and secure DOM construction. Strategic use of
         * innerHTML for static, trusted content whilst sensitive sections employ secure DOM methods.
         *
         * Implements WCAG 2.1 guidelines for accessible consent interfaces and ensures
         * compatibility with assistive technologies across different user scenarios.
         */
        function createConsentUI() {
            if (document.getElementById('ucl-privacy-banner')) return;  // Prevent duplicate UI creation and conflicts.

            const banner = document.createElement('div');
            banner.id = 'ucl-privacy-banner';
            banner.className = 'ucl-privacy-wrapper';
            banner.innerHTML = `
                <div class="ucl-privacy-banner__content">
                    <div class="ucl-privacy-banner__text">
                        <h2 class="ucl-privacy-banner__title">Privacy & Cookie Settings</h2>
                        <p id="ucl-banner-description" class="ucl-privacy-banner__description">We use cookies and similar technologies to enhance your browsing experience, analyse site traffic, and serve personalised content. You can manage your preferences at any time.</p>
                    </div>
                    <div class="ucl-privacy-banner__buttons">
                        <button type="button" class="ucl-privacy-banner__button ucl-primary-button" data-action="accept-all" aria-describedby="ucl-banner-description">Accept all cookies</button>
                        <button type="button" class="ucl-privacy-banner__button ucl-primary-button" data-action="accept-necessary" aria-describedby="ucl-banner-description">Accept necessary cookies</button>
                        <button type="button" class="ucl-privacy-banner__button ucl-privacy-banner__button--secondary" data-action="show-settings" aria-describedby="ucl-banner-description">Settings</button>
                    </div>
                </div>`;

            /*
             * Privacy policy section appended using secure DOM methods.
             * Prevents XSS vulnerabilities through configuration injection whilst
             * maintaining full functionality and accessibility compliance.
             */
            const policySection = createPrivacyPolicySection('banner');
            if (policySection) {
                banner.appendChild(policySection);
            }

            /*
             * ARIA attributes provide comprehensive semantic information for assistive technologies.
             * 'aria-live="polite"' announces banner appearance to screen readers without
             * interrupting user workflow or current focus state.
             */
            banner.setAttribute('role', 'banner');
            banner.setAttribute('aria-label', 'Cookie consent banner');
            banner.setAttribute('aria-live', 'polite');

            const modal = document.createElement('div');
            modal.id = 'ucl-privacy-modal';
            modal.className = 'ucl-privacy-wrapper';
            modal.setAttribute('role', 'dialog');
            modal.setAttribute('aria-modal', 'true');
            modal.setAttribute('aria-labelledby', 'ucl-modal-title');
            /*
             * Inline SVG icons ensure reliable rendering and eliminate external dependencies.
             * Critical for consent UI functionality which must remain operational regardless
             * of network conditions, CDN availability, or external resource blocking.
             */
            const icons = {
                necessary: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>',
                analytics: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" /></svg>',
                marketing: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" /></svg>',
                embeds: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M14.25 6.087c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.036-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959v0a.64.64 0 01-.657.643 48.39 48.39 0 01-4.163-.3c.186 1.613.293 3.25.315 4.907a.656.656 0 01-.658.663v0c-.355 0-.676-.186-.959-.401a1.647 1.647 0 00-1.003-.349c-1.036 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401v0c.31 0 .555.26.532.57a48.039 48.039 0 01-.642 5.056c1.518.19 3.058.309 4.616.354a.64.64 0 00.657-.643v0c0-.355-.186-.676-.401-.959a1.647 1.647 0 01-.349-1.003c0-1.035 1.008-1.875 2.25-1.875 1.243 0 2.25.84 2.25 1.875 0 .369-.128.713-.349 1.003-.215.283-.4.604-.4.959v0c0 .333.277.599.61.58a48.1 48.1 0 005.427-.63 48.05 48.05 0 00.582-4.717.532.532 0 00-.533-.57v0c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.035 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.37 0 .713.128 1.003.349.283.215.604.401.96.401v0a.656.656 0 00.658-.663 48.422 48.422 0 00-.37-5.36c-1.886.342-3.81.574-5.766.689a.578.578 0 01-.61-.58v0z" /></svg>',
                chevronDown: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>',
                close: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>'
            };
            modal.innerHTML = `
                <div class="ucl-privacy-modal__content">
                    <div class="ucl-privacy-modal__header">
                        <h2 id="ucl-modal-title" class="ucl-privacy-modal__title">Privacy Preferences</h2>
                        <button type="button" class="ucl-privacy-modal__close" aria-label="Close privacy preferences modal">${icons.close}</button>
                    </div>
                    <div class="ucl-privacy-modal__body">
                        <div id="ucl-modal-description" style="margin-bottom: 24px; color: var(--ucl-white); font-size: var(--ucl-font-size-base); font-family: var(--ucl-font-family);">Configure your privacy preferences for different types of cookies and tracking technologies.</div>
                        ${Object.entries(icons).filter(([k]) => k !== 'close' && k !== 'chevronDown').map(([purpose, icon]) => `
                        <div class="ucl-privacy-modal__section">
                            <div class="ucl-privacy-modal__section-header">
                                <div class="ucl-privacy-modal__section-content">
                                    <div class="ucl-privacy-modal__section-title-row">
                                        <span class="ucl-privacy-modal__section-icon">${icon}</span>
                                        <h3 class="ucl-privacy-modal__section-title">${purpose.charAt(0).toUpperCase() + purpose.slice(1)}</h3>
                                    </div>
                                    <p class="ucl-privacy-modal__section-description">${getSectionDescription(purpose)}</p>
                                    <div class="ucl-privacy-modal__section-details">
                                        <button type="button" class="ucl-privacy-modal__section-details-toggle" data-section="${purpose}" aria-expanded="false" aria-controls="${purpose}-details"><span>Learn more</span>${icons.chevronDown}</button>
                                        <div id="${purpose}-details" class="ucl-privacy-modal__section-examples" aria-hidden="true">${getSectionDetails(purpose)}</div>
                                    </div>
                                </div>
                                <label class="ucl-privacy-switch ${purpose === 'necessary' ? 'ucl-privacy-switch--disabled' : ''}" aria-label="${purpose} cookies">
                                    <input type="checkbox" class="ucl-privacy-switch__input" data-purpose="${purpose}" ${purpose === 'necessary' ? 'checked disabled' : ''}>
                                    <span class="ucl-privacy-switch__slider"></span>
                                </label>
                            </div>
                        </div>`).join('')}
                    </div>
                    <div class="ucl-privacy-modal__footer">
                        <button type="button" class="ucl-privacy-modal__button ucl-primary-button" data-action="save-settings">Save Preferences</button>
                    </div>
                </div>`;

            // Evaluate button configuration based on current URL
            const pathname = window.location.pathname;
            const btnConfig = config.settingsButton || {};
            const effectiveConfig = evaluateButtonConfig(btnConfig, pathname);

            let settingsBtn;

            // Create button only if not hidden.
            if (!effectiveConfig.hidden) {
                settingsBtn = document.createElement('button');
                settingsBtn.id = 'ucl-privacy-settings-btn';
                settingsBtn.className = 'ucl-privacy-wrapper';
                settingsBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" style="width: 24px; height: 24px;"><path stroke-linecap="round" stroke-linejoin="round" d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 0 1 1.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.559.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.894.149c-.424.07-.764.383-.929.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 0 1-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.398.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 0 1-.12-1.45l.527-.737c.25-.35.272-.806.108-1.204-.165-.397-.506-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.108-1.204l-.526-.738a1.125 1.125 0 0 1 .12-1.45l.773-.773a1.125 1.125 0 0 1 1.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894Z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>';
                settingsBtn.type = 'button';
                settingsBtn.title = 'Privacy Settings';
                settingsBtn.setAttribute('aria-label', 'Open privacy settings');

                // Apply positioning via inline styles with !important for platform-agnostic reliability.
                const pos = effectiveConfig.position;
                settingsBtn.style.setProperty('bottom', pos.bottom, 'important');
                settingsBtn.style.setProperty('z-index', String(pos.zIndex), 'important');

                // Side-dependent positioning: set active side, reset opposite side to 'auto'.
                if (pos.side === 'left') {
                    settingsBtn.style.setProperty('left', pos.horizontal, 'important');
                    settingsBtn.style.setProperty('right', 'auto', 'important');
                } else {
                    settingsBtn.style.setProperty('right', pos.horizontal, 'important');
                    settingsBtn.style.setProperty('left', 'auto', 'important');
                }

                document.body.append(banner, modal, settingsBtn);
                settingsBtnElement = settingsBtn;

                debug('Settings button created with config:', effectiveConfig);
            } else {
                debug('Settings button hidden by configuration');
                document.body.append(banner, modal);
                settingsBtnElement = null;
            }

            bannerElement = banner;
            modalElement = modal;

            // Always call addEventListeners regardless of button state.
            addEventListeners();
        }

        /**
         * Provides clear, jargon-free explanations of consent categories for informed decisions.
         * Essential for GDPR-compliant informed consent — users must understand exactly
         * what they're consenting to and the implications of their choices. Language
         * designed for general audience comprehension whilst remaining technically accurate.
         *
         * @param {string} purpose - Consent category requiring clear explanation.
         * @returns {string} User-friendly, legally compliant purpose description.
         */
        function getSectionDescription(purpose) {
            const descriptions = {
                necessary: 'Essential for the website to function properly. These cannot be disabled.',
                analytics: 'Help us improve by understanding visitor behaviour.',
                marketing: 'Deliver relevant ads and measure campaigns.',
                embeds: 'Enable third-party content and features.'
            };
            return descriptions[purpose] || '';
        }

        /**
         * Provides comprehensive details for GDPR transparency compliance requirements.
         * Expandable sections offer detailed information without overwhelming the primary
         * interface or decision flow. Includes specific examples and common provider
         * information to help users make genuinely informed decisions about their data.
         *
         * @param {string} purpose - Consent category requiring detailed explanation.
         * @returns {string} Detailed HTML content with practical examples and provider information.
         */
        function getSectionDetails(purpose) {
            const details = {
                necessary: '<strong>These cookies are required for:</strong><br>• User authentication and secure login<br>• Maintaining your session while browsing<br>• Processing secure payments<br>• Preventing security threats and fraud<br>• Remembering items in your shopping cart',
                analytics: '<strong>We use analytics to:</strong><br>• Understand which pages are most popular<br>• Monitor site performance and loading times<br>• Track visitor journeys and navigation patterns<br>• Identify technical issues and errors<br>• Measure the effectiveness of our content<br><br><strong>Common providers:</strong> Google Analytics, Matomo, Hotjar, Microsoft Clarity',
                marketing: '<strong>Marketing cookies help us:</strong><br>• Show you relevant advertisements<br>• Measure ad campaign effectiveness<br>• Limit how often you see the same ad<br>• Understand your interests and preferences<br>• Track conversions from advertising<br><br><strong>Common providers:</strong> Google Ads, Facebook Pixel, LinkedIn Insight, Twitter Ads, Amazon Advertising',
                embeds: '<strong>Important:</strong> These third-party services may set their own cookies that we cannot control.<br><br><strong>Types of embedded content:</strong><br>• YouTube and Vimeo videos<br>• Social media posts and feeds<br>• Microsoft Forms and surveys<br>• Interactive maps and location services<br>• Live chat and support widgets<br>• External booking systems'
            };
            return details[purpose] || '';
        }

        /**
         * Event handler centralisation for maintainable and accessible UI interactions.
         * Consolidates all consent-related user actions with proper event delegation,
         * keyboard navigation support, and screen reader announcements. Ensures
         * inclusive user experience across different interaction methods and abilities.
         */
        function addEventListeners() {
            bannerElement.addEventListener('click', async (e) => {
                const action = e.target.closest('button')?.dataset.action;
                if (!action) return;

                // Handle async consent updates properly.
                const button = e.target.closest('button');
                const originalText = button.textContent;
                const originalDisabled = button.disabled;

                try {
                    // Provide user feedback during consent update.
                    button.disabled = true;
                    button.textContent = 'Saving...';

                    let success = false;
                    switch (action) {
                    case 'accept-all':
                        success = await updateAllConsent({ analytics: true, marketing: true, embeds: true });
                        if (success) {
                            announceToScreenReader('All cookies accepted.');
                            log('User accepted all cookies.');
                        } else {
                            announceToScreenReader('Error saving cookie preferences. Please try again.');
                            console.error('Failed to save all-cookies consent');
                        }
                        break;
                    case 'accept-necessary':
                        success = await updateAllConsent({ analytics: false, marketing: false, embeds: false });
                        if (success) {
                            announceToScreenReader('Only necessary cookies accepted.');
                            log('User accepted only necessary cookies.');
                        } else {
                            announceToScreenReader('Error saving cookie preferences. Please try again.');
                            console.error('Failed to save necessary-only consent');
                        }
                        break;
                    case 'show-settings':
                        showModal();
                        success = true; // No async operation for show-settings.
                        break;
                    }

                    // Handle consent save failures gracefully.
                    if (!success && action !== 'show-settings') {
                        // Show user-friendly error message.
                        const errorMsg = document.createElement('div');
                        errorMsg.className = 'ucl-privacy-error-message';
                        errorMsg.textContent = 'Unable to save preferences. Please check your browser settings and try again.';
                        errorMsg.style.cssText = 'color: #ff6b6b; font-size: 14px; margin-top: 8px; text-align: center;';

                        // Remove any existing error messages.
                        const existingError = bannerElement.querySelector('.ucl-privacy-error-message');
                        if (existingError) existingError.remove();

                        // Add error message to banner.
                        bannerElement.querySelector('.ucl-privacy-banner__content').appendChild(errorMsg);

                        // Remove error message after 5 seconds.
                        setTimeout(() => {
                            if (errorMsg && errorMsg.parentNode) {
                                errorMsg.remove();
                            }
                        }, 5000);
                    }
                } catch (error) {
                    console.error('Critical error during consent action:', error);
                    announceToScreenReader('Critical error saving preferences. Please refresh the page.');
                } finally {
                    // Restore button state.
                    button.disabled = originalDisabled;
                    button.textContent = originalText;
                }
            });

            // settingsBtnElement is null when hidden by configuration (visibility.hidden = true).
            if (settingsBtnElement) {
                settingsBtnElement.addEventListener('click', showModal);
            }
            modalElement.querySelector('.ucl-privacy-modal__close').addEventListener('click', hideModal);
            modalElement.addEventListener('click', (e) => { if (e.target === modalElement) hideModal(); });  // Modal backdrop click handling for intuitive UX closure.
            modalElement.querySelector('[data-action="save-settings"]').addEventListener('click', async (e) => {
                // Handle async modal consent updates.
                const saveButton = e.target;
                const originalText = saveButton.textContent;
                const originalDisabled = saveButton.disabled;

                try {
                    // Provide user feedback during save operation.
                    saveButton.disabled = true;
                    saveButton.textContent = 'Saving...';

                    const state = {};
                    modalElement.querySelectorAll('.ucl-privacy-switch__input[data-purpose]').forEach(input => {
                        if (input.dataset.purpose !== 'necessary') {
                            state[input.dataset.purpose] = input.checked;
                        }
                    });

                    const success = await updateAllConsent(state);

                    if (success) {
                        hideModal();
                        announceToScreenReader('Privacy preferences saved.');
                        log('User saved privacy preferences.', state);
                    } else {
                        // Show error in modal instead of hiding it.
                        announceToScreenReader('Error saving privacy preferences. Please try again.');
                        console.error('Failed to save modal consent preferences');

                        // Show error message in modal.
                        const modalBody = modalElement.querySelector('.ucl-privacy-modal__body');
                        let errorMsg = modalElement.querySelector('.ucl-privacy-error-message');

                        if (!errorMsg) {
                            errorMsg = document.createElement('div');
                            errorMsg.className = 'ucl-privacy-error-message';
                            errorMsg.style.cssText = 'color: #ff6b6b; font-size: 14px; margin: 16px 0; padding: 12px; background: rgba(255, 107, 107, 0.1); border-radius: 4px; text-align: center;';
                            modalBody.appendChild(errorMsg);
                        }

                        errorMsg.textContent = 'Unable to save preferences. Please check your browser settings and try again.';

                        // Remove error message after 10 seconds.
                        setTimeout(() => {
                            if (errorMsg && errorMsg.parentNode) {
                                errorMsg.remove();
                            }
                        }, 10000);
                    }
                } catch (error) {
                    console.error('Critical error during modal consent save:', error);
                    announceToScreenReader('Critical error saving preferences. Please try again or refresh the page.');
                } finally {
                    // Restore button state.
                    saveButton.disabled = originalDisabled;
                    saveButton.textContent = originalText;
                }
            });

            modalElement.querySelectorAll('.ucl-privacy-modal__section-details-toggle').forEach(button => {
                button.addEventListener('click', () => {
                    const details = button.nextElementSibling;
                    const isExpanded = button.getAttribute('aria-expanded') === 'true';
                    button.setAttribute('aria-expanded', !isExpanded);
                    details.setAttribute('aria-hidden', isExpanded);
                    details.classList.toggle('expanded');
                    button.classList.toggle('expanded');
                });
            });
        }

        /**
         * Animates consent banner appearance with smooth, accessible transitions.
         * Uses requestAnimationFrame to ensure proper CSS transition timing and
         * prevent visual glitches or layout thrashing. Manages transition state
         * to prevent user interaction conflicts during animation sequences.
         *
         * Coordinates with persistent settings button visibility to ensure users
         * always have access to consent controls via either banner or button.
         *
         * @returns {void} Shows banner with smooth animation if not already visible.
         */
        function showBanner() {
            if (!bannerElement || bannerVisible || bannerTransitioning) return;
            bannerTransitioning = true;
            bannerElement.style.display = 'block';
            requestAnimationFrame(() => {
                bannerElement.style.transform = 'translateY(0)';
                bannerVisible = true;
                bannerTransitioning = false;
                // Button may be null if hidden by settingsButton.visibility configuration.
                if (settingsBtnElement) {
                    settingsBtnElement.classList.remove('ucl-privacy-settings-btn--visible');  // Hide persistent settings button whilst banner is displayed.
                }
            });
        }

        /**
         * Animates consent banner dismissal with proper cleanup and state management.
         * Coordinates CSS transitions with DOM visibility changes using setTimeout
         * to match animation duration precisely. Reveals persistent settings button
         * as fallback access method for users who want to modify preferences later.
         *
         * Ensures accessibility by maintaining focus management and screen reader
         * announcements during transition states. Animation duration matches CSS.
         *
         * @returns {void} Hides banner with smooth animation and shows settings button.
         */
        function hideBanner() {
            if (!bannerElement || !bannerVisible || bannerTransitioning) return;
            bannerTransitioning = true;
            bannerElement.style.transform = 'translateY(100%)';
            setTimeout(() => {
                bannerElement.style.display = 'none';
                bannerVisible = false;
                bannerTransitioning = false;
                // Button may be null if hidden by settingsButton.visibility configuration.
                if (settingsBtnElement) {
                    settingsBtnElement.classList.add('ucl-privacy-settings-btn--visible');  // Show persistent settings access.
                }
            }, 300);  // Matches CSS transition duration for smooth animation completion.
        }

        /**
         * Displays privacy settings modal with comprehensive accessibility enhancements.
         * Dynamically integrates privacy policy section and establishes proper ARIA
         * relationships for semantic clarity. Implements full keyboard navigation
         * and focus management for screen reader compatibility and inclusive access.
         *
         * Modal includes consent toggles, detailed explanations, privacy policy links,
         * and expandable sections with examples. Supports Escape key dismissal and
         * backdrop click handling for intuitive user experience.
         *
         * @returns {void} Shows modal with current consent state and focus management.
         */
        function showModal() {
            if (!modalElement || modalTransitioning) return;
            updateModalSwitches();

            /*
             * Secure privacy policy integration prevents XSS attacks whilst maintaining
             * full functionality and accessibility. Dynamic insertion allows configuration
             * updates without requiring complete UI reconstruction for better performance.
             */
            const policySection = createPrivacyPolicySection('modal');
            if (policySection) {
                const modalFooter = modalElement.querySelector('.ucl-privacy-modal__footer');
                if (modalFooter) {
                    const existingPolicy = modalElement.querySelector('.ucl-privacy-modal__policy-section');
                    if (!existingPolicy) {
                        modalFooter.parentNode.insertBefore(policySection, modalFooter);
                    }
                }
            }

            /*
             * ARIA attributes establish proper modal dialog semantics for assistive technology.
             * Ensures screen readers understand the modal structure, content relationships,
             * and navigation patterns for optimal accessibility experience.
             */
            modalElement.setAttribute('role', 'dialog');
            modalElement.setAttribute('aria-modal', 'true');
            modalElement.setAttribute('aria-labelledby', 'ucl-modal-title');
            modalElement.setAttribute('aria-describedby', 'ucl-modal-description');

            const modalTitle = modalElement.querySelector('.ucl-privacy-modal__title');
            if (modalTitle && !modalTitle.id) {
                modalTitle.id = 'ucl-modal-title';  // Ensure ID exists for proper ARIA referencing.
            }

            modalTransitioning = true;
            modalElement.classList.add('ucl-privacy-modal--active');
            document.addEventListener('keydown', handleModalKeydown);

            /*
             * Initial focus management for optimal keyboard navigation accessibility.
             * Close button provides immediate and predictable exit option for screen
             * reader users and keyboard-only navigation patterns.
             */
            const closeButton = modalElement.querySelector('.ucl-privacy-modal__close');
            if (closeButton) {
                setTimeout(() => closeButton.focus(), 100);  // Brief delay allows modal animation to complete before focus.
            }

            modalTransitioning = false;
        }

        /**
         * Dismisses privacy settings modal with proper cleanup and accessibility.
         * Removes keyboard event listeners, resets transition state, and manages
         * focus return for smooth subsequent interactions and screen reader support.
         *
         * Cleans up dynamically-inserted privacy policy sections and resets
         * modal state for future interactions without memory leaks.
         *
         * @returns {void} Hides modal and performs complete cleanup.
         */
        function hideModal() {
            if (!modalElement || modalTransitioning) return;
            modalTransitioning = true;
            modalElement.classList.remove('ucl-privacy-modal--active');
            document.removeEventListener('keydown', handleModalKeydown);
            modalTransitioning = false;
        }

        /**
         * Synchronises modal switches with current consent state for accuracy.
         * Critical for user trust — settings interface must accurately reflect
         * stored preferences at all times. Ensures perfect consistency between
         * internal consent state and UI representation, preventing user confusion
         * about current privacy settings and maintaining interface credibility.
         */
        function updateModalSwitches() {
            const consent = getConsent();
            Object.entries(consent).forEach(([purpose, allowed]) => {
                const input = modalElement.querySelector(`input[data-purpose="${purpose}"]`);
                if (input) input.checked = allowed;
            });
        }

        /**
         * Provides accessible feedback for dynamic consent changes via ARIA live regions.
         * Uses semantically appropriate live region to announce important state changes
         * to screen reader users without disrupting their current focus or navigation flow.
         * Essential for inclusive user experience and equal access to consent management.
         *
         * @param {string} message - Accessibility announcement text for screen readers.
         */
        function announceToScreenReader(message) {
            let el = document.getElementById('ucl-privacy-sr-announcer');
            if (!el) {
                el = document.createElement('div');
                el.id = 'ucl-privacy-sr-announcer';
                /*
                 * Visually hidden but screen reader accessible styling implementation.
                 * Standard web accessibility technique for off-screen content that assistive
                 * technology can access whilst remaining invisible to sighted users.
                 */
                Object.assign(el.style, { position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' });
                document.body.appendChild(el);
            }
            el.textContent = message;
        }

        /**
         * Implements standard modal keyboard interaction patterns for accessibility.
         * Escape key dismissal is universally expected behaviour for modal dialogs
         * and essential for keyboard navigation accessibility compliance.
         * Implements a focus trap so keyboard users cannot tab beyond the modal
         * to obscured page content behind it.
         *
         * @param {KeyboardEvent} e - Keyboard event for interaction pattern matching.
         */
        function handleModalKeydown(e) {
            if (e.key === 'Escape') {
                hideModal();
                return;
            }

            if (e.key === 'Tab') {
                // All standard interactive elements that participate in the tab order.
                const focusableSelectors = [
                    'a[href]',
                    'button:not([disabled])',
                    'input:not([disabled])',
                    'select:not([disabled])',
                    'textarea:not([disabled])',
                    '[tabindex]:not([tabindex="-1"])'
                ].join(', ');

                // Re-query on each keypress so dynamic state (e.g. expanded "Learn more" sections) is reflected.
                const focusableElements = Array.from(
                    modalElement.querySelectorAll(focusableSelectors)
                ).filter(el =>
                    // Skip elements inside collapsed/hidden containers.
                    !el.closest('[hidden]') &&
                    // Skip elements not rendered in the layout (display:none etc.).
                    el.offsetParent !== null
                );

                // Safety guard: nothing to trap focus within.
                if (focusableElements.length === 0) return;

                // The fence-posts of the trap — focus must not cross either boundary.
                const firstFocusable = focusableElements[0];
                const lastFocusable  = focusableElements[focusableElements.length - 1];

                if (e.shiftKey) {
                    // Shift+Tab at the top boundary: wrap backwards to the last element.
                    if (document.activeElement === firstFocusable) {
                        e.preventDefault(); // Prevent focus leaving the modal.
                        lastFocusable.focus();
                    }
                } else {
                    // Tab at the bottom boundary: wrap forwards to the first element.
                    if (document.activeElement === lastFocusable) {
                        e.preventDefault(); // Prevent focus leaving the modal.
                        firstFocusable.focus();
                    }
                }
            }
        }

        /**
         * Safely checks if a placeholder already exists for a given source URL.
         * Uses O(1) cache lookup to avoid repeated DOM queries for performance.
         * Falls back to DOM scan only on cache miss to ensure accuracy.
         *
         * @param {string} src - The source URL to check for existing placeholders.
         * @returns {boolean} True if placeholder exists, false otherwise.
         */
        function placeholderExists(src) {
            if (!src) return false;

            // Fast O(1) cache lookup for known placeholders
            if (placeholderCache.has(src)) {
                return true;
            }

            // Fallback DOM scan only on cache miss to verify accuracy.
            // Uses attribute-based filtering to prevent CSS selector injection.
            const placeholders = document.querySelectorAll('[data-placeholder-for]');
            for (const placeholder of placeholders) {
                if (placeholder.getAttribute('data-placeholder-for') === src) {
                    // Update cache with discovered placeholder
                    placeholderCache.add(src);
                    return true;
                }
            }
            return false;
        }

        /**
         * Initialises placeholders for pre-existing blocked content with deduplication.
         * Handles iframes that were blocked during initial page load before UI
         * initialisation completed. Implements comprehensive deduplication logic
         * through DOM state checking to prevent multiple placeholders for identical content.
         */
        function scanAndApplyPlaceholders() {
            document.querySelectorAll('iframe[data-blocked-purpose="embeds"]').forEach(iframe => {
                // Check if placeholder already exists anywhere after the iframe
                const src = iframe.getAttribute('data-blocked-src');
                if (placeholderExists(src)) return;  // Comprehensive deduplication check prevents duplicate placeholders.

                const placeholder = createIframePlaceholder(iframe);
                iframe.parentNode.insertBefore(placeholder, iframe.nextSibling);
            });
        }

        /**
         * Content restoration engine for post-consent activation and user experience.
         * Reconstructs blocked scripts and restores iframe sources when user consent
         * is granted for specific categories. Uses complete element replacement for
         * scripts to ensure reliable execution across different browser environments.
         *
         * Restoration process:
         * 1. Sequential processing by consent category to prevent race conditions
         * 2. Complete script replacement with original attributes and content
         * 3. Enhanced load verification with timeout handling
         * 4. Iframe source restoration with placeholder cleanup
         * 5. Comprehensive class and attribute cleanup
         * 6. Memory leak prevention through proper resource management
         *
         * Includes immediate verification pass and custom event dispatch for
         * external monitoring and debugging capabilities.
         *
         * @returns {void} Restores all content for consented categories.
         */
        function restoreContent() {
            log('restoreContent() called');
            const consent = getConsent();
            log('Restoring content for consent:', consent);

            // Sequential processing to avoid race conditions between script executions.
            const purposeEntries = Object.entries(consent).filter(([_, allowed]) => allowed);

            // Process each purpose category sequentially to prevent timing conflicts.
            purposeEntries.forEach(([purpose, allowed]) => {
                if (allowed) {
                    const blockedScripts = document.querySelectorAll(`.privacy-blocked-${purpose}`);
                    log(`Found ${blockedScripts.length} blocked scripts for purpose: ${purpose}`);

                    /*
                     * Enhanced script restoration with proper execution guarantees and race condition prevention.
                     * Uses sequential processing and load verification to ensure reliable execution
                     * across different browsers and CMS environments without timing conflicts.
                     */
                    const scriptsArray = Array.from(blockedScripts);

                    // Process scripts sequentially within each purpose to prevent execution race conditions.
                    scriptsArray.forEach((script, scriptIndex) => {
                        const originalSrc = script.dataset.originalSrc;
                        const originalType = script.dataset.originalType || 'text/javascript';
                        const scriptContent = script.textContent || script.innerHTML;
                        const scriptId = script.id || `script-${purpose}-${scriptIndex}`;

                        debug('Restoring script:', {
                            id: scriptId,
                            src: originalSrc,
                            type: originalType,
                            hasTextContent: !!scriptContent,
                            element: script
                        });

                        if (originalSrc) {
                            debug('Creating new script element for:', originalSrc);

                            // Strategy 1: Sequential DOM insertion with proper timing control.
                            const newScript = document.createElement('script');
                            newScript.type = originalType;

                            // Preserve original ID for debugging and identification.
                            if (script.id) {
                                newScript.id = script.id + '-restored';
                            }

                            // Copy essential attributes first with enhanced validation.
                            if (script.hasAttribute('async')) {
                                newScript.async = true;
                            }
                            if (script.hasAttribute('defer')) {
                                newScript.defer = true;
                            }
                            if (script.hasAttribute('crossorigin')) {
                                newScript.crossOrigin = script.getAttribute('crossorigin');
                            }
                            if (script.hasAttribute('integrity')) {
                                newScript.integrity = script.getAttribute('integrity');
                            }

                            // Copy other important attributes (excluding privacy-specific ones).
                            Array.from(script.attributes).forEach(attr => {
                                if (!attr.name.startsWith('data-blocked') &&
                                    !attr.name.startsWith('data-original') &&
                                    !attr.name.includes('privacy-blocked') &&
                                    attr.name !== 'type' &&
                                    attr.name !== 'src' &&
                                    attr.name !== 'id' &&
                                    attr.name !== 'async' &&
                                    attr.name !== 'defer' &&
                                    attr.name !== 'crossorigin' &&
                                    attr.name !== 'integrity') {
                                    try {
                                        newScript.setAttribute(attr.name, attr.value);
                                    } catch (e) {
                                        // Skip attributes that can't be set
                                        debug('Could not copy attribute:', attr.name, e);
                                    }
                                }
                            });

                            // Include inline content if present (for GTM dataLayer initialization).
                            if (scriptContent && scriptContent.trim()) {
                                newScript.textContent = scriptContent;
                            }

                            // Enhanced restoration with comprehensive load verification and memory leak prevention.
                            let restoreComplete = false;
                            const timeoutKey = `script-${originalSrc}-${Date.now()}`;

                            const handleLoad = () => {
                                if (!restoreComplete) {
                                    restoreComplete = true;

                                    // Clean up timeout tracking.
                                    restorationTracker.clearTimeout(timeoutKey);

                                    // Remove event listeners from old script.
                                    restorationTracker.removeEventListeners(newScript);

                                    log('Script loaded successfully:', originalSrc);

                                    // Dispatch custom event for script load completion.
                                    window.dispatchEvent(new CustomEvent('ucl-privacy-script-restored', {
                                        detail: { src: originalSrc, purpose: purpose, success: true }
                                    }));
                                }
                            };

                            const handleError = (error) => {
                                if (!restoreComplete) {
                                    restoreComplete = true;

                                    // Clean up timeout tracking.
                                    restorationTracker.clearTimeout(timeoutKey);

                                    // Remove event listeners from old script.
                                    restorationTracker.removeEventListeners(newScript);

                                    console.warn('[UCL Privacy UI] Script load failed:', originalSrc, error);

                                    // Dispatch custom event for script load failure.
                                    window.dispatchEvent(new CustomEvent('ucl-privacy-script-restored', {
                                        detail: { src: originalSrc, purpose: purpose, success: false, error: error }
                                    }));
                                }
                            };

                            // Track event listeners for proper cleanup.
                            restorationTracker.trackEventListener(newScript, handleLoad);
                            restorationTracker.trackEventListener(newScript, handleError);

                            // Set up timeout for load verification with proper tracking.
                            const restoreTimeout = setTimeout(() => {
                                if (!restoreComplete) {
                                    restoreComplete = true;

                                    // Remove event listeners on timeout.
                                    restorationTracker.removeEventListeners(newScript);

                                    console.warn('[UCL Privacy UI] Script load timeout after 5 seconds:', originalSrc);

                                    // Dispatch timeout event.
                                    window.dispatchEvent(new CustomEvent('ucl-privacy-script-restored', {
                                        detail: { src: originalSrc, purpose: purpose, success: false, error: 'timeout' }
                                    }));
                                }
                            }, 5000);

                            // Track timeout for cleanup.
                            restorationTracker.addTimeout(timeoutKey, restoreTimeout);

                            newScript.addEventListener('load', handleLoad);
                            newScript.addEventListener('error', handleError);

                            // Strategy 2: Controlled insertion with proper parent element handling.
                            const insertionParent = script.parentNode || document.head;

                            // Use try-catch for DOM manipulation to handle edge cases.
                            try {
                                // Remove the blocked script first to avoid conflicts.
                                script.remove();

                                // Insert new script with proper positioning.
                                insertionParent.appendChild(newScript);

                                // Set src after insertion to trigger load - this is crucial for execution.
                                // Use requestAnimationFrame for proper timing in the browser's rendering cycle.
                                requestAnimationFrame(() => {
                                    try {
                                        newScript.src = originalSrc;
                                        debug('Script src set, loading initiated:', originalSrc);
                                    } catch (srcError) {
                                        console.error('[UCL Privacy UI] Error setting script src:', originalSrc, srcError);
                                        handleError(srcError);
                                    }
                                });

                                debug('Enhanced script restoration initiated for:', originalSrc);
                            } catch (domError) {
                                console.error('[UCL Privacy UI] DOM manipulation error during script restoration:', originalSrc, domError);
                                clearTimeout(restoreTimeout);
                                handleError(domError);
                            }
                        } else if (scriptContent && scriptContent.trim()) {
                            // For inline scripts, use enhanced dynamic execution strategy.
                            debug('Restoring inline script with content length:', scriptContent.length);

                            try {
                                // Strategy 1: Clean attribute restoration with validation.
                                script.type = originalType;

                                // Comprehensive class cleanup for inline scripts.
                                // Remove all privacy-blocking related classes.
                                script.classList.remove('privacy-blocked', `privacy-blocked-${purpose}`);

                                // Additional cleanup for any remaining privacy-blocked classes
                                const classList = Array.from(script.classList);
                                classList.forEach(className => {
                                    if (className.startsWith('privacy-blocked')) {
                                        script.classList.remove(className);
                                    }
                                });

                                // Clean up empty class attribute if no classes remain.
                                if (script.classList.length === 0 && script.hasAttribute('class')) {
                                    script.removeAttribute('class');
                                }

                                script.removeAttribute('data-blocked-purpose');
                                script.removeAttribute('data-blocked-service');
                                script.removeAttribute('data-blocked-source');
                                script.removeAttribute('data-original-type');
                                script.removeAttribute('data-original-src');

                                // Strategy 2: Enhanced re-execution for inline scripts with error handling
                                if (originalType === 'text/javascript' || originalType === 'application/javascript') {
                                    try {
                                        // Create a new script element to force execution.
                                        const execScript = document.createElement('script');
                                        execScript.type = originalType;
                                        execScript.textContent = scriptContent;

                                        // Add identification for debugging.
                                        if (script.id) {
                                            execScript.setAttribute('data-restored-from', script.id);
                                        }

                                        const insertionPoint = script.parentNode || document.head;

                                        // Insert and execute with error handling.
                                        insertionPoint.insertBefore(execScript, script.nextSibling);

                                        log('Inline script re-executed successfully');

                                        // Dispatch success event.
                                        window.dispatchEvent(new CustomEvent('ucl-privacy-script-restored', {
                                            detail: { type: 'inline', purpose: purpose, success: true }
                                        }));
                                    } catch (execError) {
                                        console.error('[UCL Privacy UI] Error executing inline script:', execError);

                                        // Dispatch failure event.
                                        window.dispatchEvent(new CustomEvent('ucl-privacy-script-restored', {
                                            detail: { type: 'inline', purpose: purpose, success: false, error: execError }
                                        }));
                                    }
                                }

                                debug('Restored inline script attributes');
                            } catch (error) {
                                console.error('[UCL Privacy UI] Error restoring inline script:', error);

                                // Dispatch failure event.
                                window.dispatchEvent(new CustomEvent('ucl-privacy-script-restored', {
                                    detail: { type: 'inline', purpose: purpose, success: false, error: error }
                                }));
                            }
                        } else {
                            debug('Skipping script with no src or content:', script);
                        }
                    });

                    /*
                     * Enhanced iframe restoration with comprehensive cleanup.
                     * Removes all blocked attributes and restores proper iframe functionality.
                     */
                    document.querySelectorAll(`iframe[data-blocked-purpose="${purpose}"]`).forEach(iframe => {
                        const blockedSrc = iframe.getAttribute('data-blocked-src');
                        if (blockedSrc) {
                        // Restore iframe src and clean up blocked attributes.
                            iframe.src = blockedSrc;
                            iframe.style.display = '';
                            iframe.removeAttribute('data-blocked-src');
                            iframe.removeAttribute('data-blocked-purpose');
                            iframe.removeAttribute('data-blocked-actual-url');

                            debug('Restored iframe:', blockedSrc);
                        }

                        // Find and remove associated placeholder.
                        const placeholder = iframe.nextElementSibling;
                        if (placeholder && placeholder.classList.contains('ucl-privacy-placeholder')) {
                        // Clean up cache when placeholder is removed.
                            const placeholderSrc = placeholder.getAttribute('data-placeholder-for');
                            if (placeholderSrc) {
                                placeholderCache.delete(placeholderSrc);
                            }
                            placeholder.remove();
                            debug('Removed placeholder for:', placeholderSrc);
                        }
                    });
                }
            });

            // RESTORATION FIX: Immediate verification of restoration completeness.
            requestAnimationFrame(() => {
                // Additional pass to clean up any elements that might have missed cleanup.
                // This ensures scripts that were successfully restored but still have blocking classes are cleaned.
                Object.entries(consent).forEach(([purpose, allowed]) => {
                    if (allowed) {
                        // Clean up any remaining elements with privacy-blocked classes for consented purposes.
                        const remainingBlockedForPurpose = document.querySelectorAll(`.privacy-blocked-${purpose}`);
                        remainingBlockedForPurpose.forEach(element => {
                            if (element.tagName === 'SCRIPT') {
                                debug('Final cleanup pass for consented script:', element, 'purpose:', purpose);
                                element.classList.remove('privacy-blocked', `privacy-blocked-${purpose}`);

                                // Comprehensive class cleanup.
                                const classList = Array.from(element.classList);
                                classList.forEach(className => {
                                    if (className.startsWith('privacy-blocked')) {
                                        element.classList.remove(className);
                                    }
                                });

                                // Clean up empty class attribute if no classes remain.
                                if (element.classList.length === 0 && element.hasAttribute('class')) {
                                    element.removeAttribute('class');
                                }

                                element.removeAttribute('data-blocked-purpose');
                                element.removeAttribute('data-blocked-service');
                                element.removeAttribute('data-blocked-source');
                            }
                        });
                    }
                });

                const remainingBlocked = document.querySelectorAll('.privacy-blocked, [data-blocked-purpose]');
                if (remainingBlocked.length > 0) {
                    console.warn('Some content may still be blocked after restoration:', remainingBlocked.length, 'elements');

                    // Additional cleanup pass for any remaining blocked elements.
                    remainingBlocked.forEach(element => {
                        const purpose = element.getAttribute('data-blocked-purpose');
                        if (purpose && consent[purpose]) {
                            debug('Additional cleanup for missed element:', element);

                            if (element.tagName === 'SCRIPT') {
                                // Comprehensive class cleanup for missed elements.
                                element.classList.remove('privacy-blocked', `privacy-blocked-${purpose}`);

                                // Additional cleanup for any remaining privacy-blocked classes.
                                const classList = Array.from(element.classList);
                                classList.forEach(className => {
                                    if (className.startsWith('privacy-blocked')) {
                                        element.classList.remove(className);
                                    }
                                });

                                // Clean up empty class attribute if no classes remain.
                                if (element.classList.length === 0 && element.hasAttribute('class')) {
                                    element.removeAttribute('class');
                                }

                                element.removeAttribute('data-blocked-purpose');
                                element.removeAttribute('data-blocked-service');
                                element.removeAttribute('data-blocked-source');

                                const originalType = element.getAttribute('data-original-type');
                                if (originalType) {
                                    element.type = originalType;
                                    element.removeAttribute('data-original-type');
                                }

                                const originalSrc = element.getAttribute('data-original-src');
                                if (originalSrc) {
                                    element.src = originalSrc;
                                    element.removeAttribute('data-original-src');
                                }
                            } else if (element.tagName === 'IFRAME') {
                                const blockedSrc = element.getAttribute('data-blocked-src');
                                if (blockedSrc) {
                                    element.src = blockedSrc;
                                    element.style.display = '';
                                    element.removeAttribute('data-blocked-src');
                                    element.removeAttribute('data-blocked-purpose');
                                    element.removeAttribute('data-blocked-actual-url');
                                }
                            }
                        }
                    });
                } else {
                    log('Content restoration completed successfully - no blocked elements remaining');
                }
            });
        }

        /*
         * UI initialisation orchestration with consent state awareness.
         * Coordinates component setup, placeholder generation, and banner display
         * based on user consent history. Event-driven architecture maintains
         * loose coupling between UI and consent logic.
         */
        createConsentUI();
        scanAndApplyPlaceholders();

        /*
         * Conditional banner display respects user consent history.
         * New users see banner after delay; returning users see persistent settings access.
         */
        if (!hasUserConsented() && config.options.showBanner) {
            setTimeout(showBanner, config.options.bannerDelay);
        } else {
            // Button may be null if hidden by settingsButton.visibility configuration.
            // When null, users access settings via other means (e.g., site-provided link calling showSettings()).
            if (settingsBtnElement) {
                settingsBtnElement.classList.add('ucl-privacy-settings-btn--visible');
            }
        }

        /*
         * Event-driven response to consent state changes.
         * Maintains UI synchronisation with consent decisions and triggers content restoration.
         * Enhanced with immediate verification instead of delayed timeouts for better user experience.
         */
        window.addEventListener('ucl-privacy-consent-updated', (event) => {
            log('Consent updated event received');
            debug('Event details:', event.detail);

            try {
                hideBanner();

                // Use immediate restoration with built-in verification.
                requestAnimationFrame(() => {
                    log('Starting immediate content restoration after consent update');
                    restoreContent();

                    // Additional immediate verification pass.
                    requestAnimationFrame(async () => {
                        await verifyRestorationCompleteness();
                    });
                });
            } catch (error) {
                console.error('Error during consent update handling:', error);
            }
        });

        /*
         * Dynamic placeholder generation for runtime-blocked content with deduplication.
         * Handles iframes blocked after initial page load through mutation observer
         * events and lazy-loading scenarios. Implements comprehensive deduplication
         * logic to prevent multiple placeholders for identical content sources.
         */
        window.addEventListener('ucl-privacy-iframe-blocked', (e) => {
            const { iframe } = e.detail;
            const src = iframe.getAttribute('data-blocked-src');

            // Prevent duplicate placeholders using safe attribute checking.
            if (placeholderExists(src)) return;

            const placeholder = createIframePlaceholder(iframe);
            iframe.parentNode.insertBefore(placeholder, iframe.nextSibling);
        });

        /**
         * Verifies restoration completeness and reports status.
         * Provides immediate feedback on restoration success without relying on timeouts.
         * Dispatches events for external monitoring and debugging capabilities.
         */
        async function verifyRestorationCompleteness() {
            const consent = getConsent();
            const consentedPurposes = Object.entries(consent)
                .filter(([purpose, allowed]) => allowed && purpose !== 'necessary')
                .map(([purpose]) => purpose);

            if (consentedPurposes.length === 0) {
                log('No additional consent granted, verification skipped');
                return { success: true, remaining: 0, purposes: [] };
            }

            // Small delay to allow DOM updates to settle.
            await new Promise(resolve => setTimeout(resolve, 50));

            // Check for remaining blocked elements that should have been restored.
            const remainingBlocked = document.querySelectorAll('.privacy-blocked, [data-blocked-purpose]');
            const shouldBeRestored = Array.from(remainingBlocked).filter(element => {
                const purpose = element.getAttribute('data-blocked-purpose') ||
                              (element.className.match(/privacy-blocked-([a-z]+)/) || [])[1];
                return purpose && consent[purpose];
            });

            const verificationResult = {
                success: shouldBeRestored.length === 0,
                remaining: shouldBeRestored.length,
                total: remainingBlocked.length,
                purposes: consentedPurposes,
                timestamp: Date.now()
            };

            if (verificationResult.success) {
                log('Restoration verification passed - all consented content restored');
            } else {
                console.warn(`Restoration verification failed - ${shouldBeRestored.length} elements still blocked:`, shouldBeRestored);

                // Attempt immediate recovery for failed restorations.
                shouldBeRestored.forEach(element => {
                    const purpose = element.getAttribute('data-blocked-purpose') ||
                                  (element.className.match(/privacy-blocked-([a-z]+)/) || [])[1];
                    if (purpose) {
                        debug('Attempting immediate recovery for element:', element, 'purpose:', purpose);

                        if (element.tagName === 'SCRIPT') {
                            // Force cleanup of blocked state with comprehensive class removal.
                            element.classList.remove('privacy-blocked', `privacy-blocked-${purpose}`);

                            // Additional cleanup for any remaining privacy-blocked classes.
                            const classList = Array.from(element.classList);
                            classList.forEach(className => {
                                if (className.startsWith('privacy-blocked')) {
                                    element.classList.remove(className);
                                }
                            });

                            element.removeAttribute('data-blocked-purpose');
                            element.removeAttribute('data-blocked-service');
                            element.removeAttribute('data-blocked-source');

                            // Clean up malformed class attribute if empty.
                            if (element.classList.length === 0 && element.hasAttribute('class')) {
                                element.removeAttribute('class');
                            }

                            const originalType = element.getAttribute('data-original-type');
                            if (originalType) {
                                element.type = originalType;
                                element.removeAttribute('data-original-type');
                            }

                            const originalSrc = element.getAttribute('data-original-src');
                            if (originalSrc) {
                                element.src = originalSrc;
                                element.removeAttribute('data-original-src');
                                debug('Recovered external script:', originalSrc);
                            } else {
                                debug('Recovered inline script');
                            }
                        } else if (element.tagName === 'IFRAME') {
                            const blockedSrc = element.getAttribute('data-blocked-src');
                            if (blockedSrc) {
                                element.src = blockedSrc;
                                element.style.display = '';
                                element.removeAttribute('data-blocked-src');
                                element.removeAttribute('data-blocked-purpose');
                                element.removeAttribute('data-blocked-actual-url');
                                debug('Recovered iframe:', blockedSrc);
                            }
                        }
                    }
                });
            }

            // Dispatch verification event for external monitoring.
            window.dispatchEvent(new CustomEvent('ucl-privacy-restoration-verified', {
                detail: verificationResult
            }));

            return verificationResult;
        }

        /**
         * UCLPrivacyUI global API for programmatic interface control.
         * Provides controlled access to UI functions whilst maintaining proper
         * encapsulation of internal state and validation. Enables integration
         * with custom triggers, external consent platforms, and debugging tools.
         *
         * All methods include proper error handling and state validation to prevent
         * UI corruption from external calls. Designed for safe use by third-party
         * integrations and development tools.
         *
         * @namespace UCLPrivacyUI
         * @global
         */
        window.UCLPrivacyUI = {
            showSettings: showModal,
            hideBanner: hideBanner,

            /**
             * Returns effective platform mapping for debugging and transparency.
             * Combines built-in platform mappings with user configuration extensions
             * to provide complete hostname-to-display-name mapping visibility.
             *
             * Platform mappings control how service names appear in consent placeholders,
             * converting technical hostnames like 'www.youtube.com' to user-friendly
             * names like 'YouTube' for better user comprehension and trust.
             *
             * @returns {object} Complete platform mapping with hostname keys and display name values.
             * @example
             * const mapping = UCLPrivacyUI.getPlatformMapping();
             * console.log(mapping['youtube.com']); // 'YouTube'
             */
            getPlatformMapping: () => ({ ...extendedPlatformMapping }),

            /**
             * Returns effective button configuration for debugging.
             * Shows the evaluated button config after URL pattern matching and cascade.
             * Useful for testing and verifying button positioning rules.
             *
             * Note: evaluateButtonConfig() creates fresh objects internally, so this
             * method is safe for external use without additional defensive copying.
             * The internal object creation prevents callers from mutating shared state.
             *
             * @returns {object} Effective configuration with visibility and position
             * @example
             * const config = UCLPrivacyUI.getEffectiveButtonConfig();
             * console.log(config.position.side); // 'left' or 'right'
             * console.log(config.hidden); // true or false
             */
            getEffectiveButtonConfig: () => {
                const pathname = window.location.pathname;
                const btnConfig = config.settingsButton || {};
                return evaluateButtonConfig(btnConfig, pathname);
            },

            /**
             * IMMEDIATE VERIFICATION FIX: Verifies restoration completeness immediately.
             * Provides real-time restoration status without waiting for timeouts.
             * Useful for debugging and external monitoring of restoration success.
             *
             * @returns {object} Verification result with success status and metrics.
             */
            verifyRestoration: verifyRestorationCompleteness,

            /**
             * Cleanup function for proper resource management.
             * Cleans up all tracked resources including timeouts, blob URLs,
             * event listeners, and cached patterns to prevent memory leaks.
             * Should be called when the UI is being destroyed or during page unload.
             *
             * @returns {boolean} True if cleanup was successful.
             */
            cleanup: () => {
                try {
                    restorationTracker.cleanup();
                    placeholderCache.clear();
                    patternCache.clear();  // Prevent memory leak from cached regex patterns.
                    log('Cleanup completed successfully');
                    return true;
                } catch (error) {
                    console.error('Error during cleanup:', error);
                    return false;
                }
            }
        };

        log('UCL Privacy UI initialised.');
    }

    /*
     * DOM readiness detection for optimal UI timing.
     * UI can be loaded after blocker for better page performance whilst
     * maintaining proper initialisation order through event coordination.
     */
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeWhenReady);
    } else {
        initializeWhenReady();  // DOM already ready - initialise immediately.
    }
})();
